import { useEffect, useMemo, useState } from 'react'
import './Footer.css'
import { useTranslation } from '../../utils/i18n'
import { companyProfile } from '@/config/companyProfile'
import { fetchSiteSettings } from '@/services/siteSettings'

const SOCIAL_ICONS = {
  facebook: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22 12a10 10 0 1 0-11.56 9.86v-6.98H7.9V12h2.54V9.74c0-2.5 1.5-3.89 3.8-3.89 1.1 0 2.25.2 2.25.2v2.48h-1.27c-1.25 0-1.64.77-1.64 1.56V12h2.79l-.45 2.88h-2.34v6.98A10 10 0 0 0 22 12" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7Zm5 3.5A4.5 4.5 0 1 1 7.5 12 4.5 4.5 0 0 1 12 7.5Zm0 2A2.5 2.5 0 1 0 14.5 12 2.5 2.5 0 0 0 12 9.5Zm5.25-3.5a1 1 0 1 1-1 1 1 1 0 0 1 1-1Z" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M20.45 20.45h-3.55v-5.41c0-1.29-.02-2.95-1.8-2.95s-2.07 1.4-2.07 2.86v5.5H9.48V9h3.4v1.56h.05a3.73 3.73 0 0 1 3.36-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.34 7.43a2.06 2.06 0 1 1 2.06-2.05 2.05 2.05 0 0 1-2.06 2.05ZM7.11 20.45H3.58V9h3.53v11.45Z" />
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M21 8.43a5.85 5.85 0 0 1-3.76-1.3v6.4a6.62 6.62 0 1 1-6.62-6.62 6.4 6.4 0 0 1 .77.05V9.7a3.77 3.77 0 1 0 2.72 3.62V2h3.08A5.85 5.85 0 0 0 21 5.77Z" />
    </svg>
  )
}

export default function Footer () {
  const { t } = useTranslation()
  const logoInitial = companyProfile.name.charAt(0)
  const [logoFailed, setLogoFailed] = useState(false)
  const [socials, setSocials] = useState({})
  const logoSrc = '/assets/brand/logo-serenita-mark-cropped.png'
  const { contact, address } = companyProfile
  const currentYear = new Date().getFullYear()

  const safeAbsoluteHttpUrl = (value) => {
    if (typeof value !== 'string') return null
    const trimmed = value.trim()
    if (!trimmed) return null
    try {
      const url = new URL(trimmed)
      if (url.protocol === 'http:' || url.protocol === 'https:') return trimmed
    } catch {
      // ignore
    }
    return null
  }

  useEffect(() => {
    let ignore = false
    const load = async () => {
      const settings = await fetchSiteSettings()
      if (ignore) return
      setSocials(settings?.social && typeof settings.social === 'object' ? settings.social : {})
    }
    load()
    return () => { ignore = true }
  }, [])

  const socialPlatforms = useMemo(() => ([
    { key: 'facebook', label: 'Facebook', href: safeAbsoluteHttpUrl(socials.facebook) },
    { key: 'instagram', label: 'Instagram', href: safeAbsoluteHttpUrl(socials.instagram) },
    { key: 'linkedin', label: 'LinkedIn', href: safeAbsoluteHttpUrl(socials.linkedin) },
    { key: 'tiktok', label: 'TikTok', href: safeAbsoluteHttpUrl(socials.tiktok) },
    { key: 'youtube', label: 'YouTube', href: safeAbsoluteHttpUrl(socials.youtube) },
    { key: 'whatsapp', label: 'WhatsApp', href: safeAbsoluteHttpUrl(socials.whatsapp) }
  ].filter(p => Boolean(p.href))), [socials])

  return (
    <footer className="site-footer">
      <div className="container footer-grid">
        <div>
          {!logoFailed
            ? (
              <img
                className="brand-logo"
                src={logoSrc}
                alt={companyProfile.name}
                loading="lazy"
                decoding="async"
                onError={() => setLogoFailed(true)}
              />
              )
            : (
              <div className="logo-mark" aria-hidden="true">
                <span className="logo-fallback" aria-hidden="true">{logoInitial}</span>
              </div>
              )}
          <p>{companyProfile.name} — {t('nav.services')}</p>
        </div>
        <div>
          <h4>{t('footer.contacts', 'Contacts')}</h4>
          <p>{contact.phone}</p>
          <p>{contact.email}</p>
        </div>
        <div>
          <h4>{t('footer.address', 'Adresse')}</h4>
          <p>{address.line1}</p>
          {address.line2 && <p>{address.line2}</p>}
          <p>{address.postalCode ? `${address.postalCode} ${address.city}` : address.city} — {address.country}</p>
        </div>
        <div>
          <h4>{t('footer.social', 'Social')}</h4>
          <div className="socials">
            {socialPlatforms.length ? socialPlatforms.map(platform => {
              const icon = SOCIAL_ICONS[platform.key]
              if (!icon) return null
              return (
                <a
                  key={platform.key}
                  className="social-link"
                  href={platform.href}
                  aria-label={platform.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {icon}
                </a>
              )
            }) : (
              <span className="text-sm text-neutral-500">{t('footer.socialEmpty', '—')}</span>
            )}
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <span className="footer-legal">© {currentYear} {companyProfile.legalName}. {t('footer.rights', 'Tous droits réservés.')}</span>
        <div className="footer-links">
          <a href="/privacy">{t('footer.privacy', 'Confidentialité')}</a>
          <a href="/cookies">{t('footer.cookies', 'Cookies')}</a>
          <a href="/terms">{t('footer.terms', 'Conditions')}</a>
          <a href="/legal-updates">{t('footer.legal_updates', 'Veille légale')}</a>
          <a href="/faq">{t('footer.faq', 'FAQ')}</a>
        </div>
        <a className="footer-powered" href="https://qvadis.it" target="_blank" rel="noopener noreferrer">
          Powered by QVADIS
        </a>
      </div>
    </footer>
  )
}
